import { Check, Undo2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { Button } from '@/components/ui/button'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

export interface LastCapture {
  sku: string
  name: string
  /** The SKU's running total after this capture. */
  total: number
  /** What this entry added, or null when it replaced the total instead. */
  added: number | null
  /** The total before this capture — what "Rückgängig" restores. Null means there was none. */
  previousTotal: number | null
}

interface CountLastCaptureBandProps {
  capture: LastCapture
  pending: boolean
  onUndo: () => void
}

/**
 * The confirmation of the last capture, on the capture screen itself.
 *
 * A scan followed by a quantity used to leave nothing behind on screen, so the counter could not
 * tell what the article's total had become, and a mistyped digit was only findable by hunting the
 * captured list. The band names the article, its resulting total, and offers a one-tap undo.
 *
 * Undo is a capture like any other — it sends the previous total back under a fresh `captureId`,
 * or removes the row when there was no previous total — so it goes through the same guards and
 * cannot itself double-count.
 */
export function CountLastCaptureBand({
  capture,
  pending,
  onUndo,
}: CountLastCaptureBandProps) {
  const { t } = useTranslation('inventory-count')

  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-emerald-200 bg-emerald-50 px-4 py-2 dark:border-emerald-900 dark:bg-emerald-950/40">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-600">
        <Check className="h-4 w-4 text-white" aria-hidden />
      </span>
      <div className="min-w-0 flex-1" aria-live="polite">
        <p className="truncate text-sm font-semibold text-emerald-950 dark:text-emerald-50">
          {capture.name}
        </p>
        <p className="truncate text-xs tabular-nums text-emerald-800 dark:text-emerald-200">
          {capture.added != null
            ? t('capture.lastAdded', {
                total: formatStockDisplay(capture.total),
                added: formatStockDisplay(capture.added),
              })
            : t('capture.lastReplaced', {
                total: formatStockDisplay(capture.total),
              })}
        </p>
      </div>
      <Button
        variant="outline"
        className="h-11 shrink-0"
        disabled={pending}
        onClick={onUndo}
      >
        <Undo2 className="h-4 w-4" />
        {t('capture.undo')}
      </Button>
    </div>
  )
}
